'use client';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, IconButton, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { carouselArrowSx } from '@odys/design-system/components/carouselArrowSx';
import { radius } from '@odys/design-system/tokens/radius';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { DmcMediaDto } from '@/app/api/lib/dmcs/dmc.types';

/**
 * Carousel des médias de présentation d'une DMC (#2440) : liste ordonnée mixte
 * images + vidéos embarquées, là où le bloc n'affichait qu'un seul média.
 *
 * Distinct de `ImageCarousel` (cards produit/itinéraire) sur deux points qui
 * découlent des vidéos :
 *   - **une seule slide est montée à la fois**. `ImageCarousel` empile toutes
 *     ses images en `opacity: 0` pour précharger ; ici la même approche
 *     monterait N iframes YouTube/Vimeo dès l'affichage de la page (requêtes
 *     réseau et lecteurs invisibles pour des médias que personne ne regardera).
 *   - **flèches toujours visibles** plutôt que révélées au survol : le média
 *     occupe une demi-colonne pleine, la navigation doit être découvrable sans
 *     survol (et au clavier / sur tactile).
 *
 * Un seul média → aucun contrôle rendu, le bloc est visuellement identique à
 * l'ancien affichage simple.
 */
export function PresentationMediaCarousel({ media }: { media: ReadonlyArray<DmcMediaDto> }) {
    const t = useTranslations('dmcPage.presentation');
    const [requested, setRequested] = useState(0);

    const count = media.length;
    if (count === 0) return null;

    // Clamp partagé par la slide rendue, les puces et `data-active-index` : si la
    // liste rétrécissait entre deux rendus, ils resteraient d'accord entre eux.
    const active = Math.min(requested, count - 1);
    const current = media[active];
    const hasControls = count > 1;
    const go = (delta: number) => () => setRequested((active + delta + count) % count);

    return (
        <Box
            data-testid="presentation-media-carousel"
            data-active-index={active}
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: radius.xl,
                overflow: 'hidden',
                bgcolor: 'grey.200',
            }}
        >
            {current.type === 'video' ? (
                <VideoSlide url={current.url} title={t('videoTitle')} />
            ) : (
                <Box
                    component="img"
                    data-testid="presentation-media-image"
                    src={current.url}
                    alt=""
                    decoding="async"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            )}

            {hasControls && (
                <>
                    <IconButton
                        data-testid="presentation-media-prev"
                        aria-label={t('carousel.prev')}
                        size="small"
                        onClick={go(-1)}
                        sx={carouselArrowSx('left', { alwaysVisible: true })}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        data-testid="presentation-media-next"
                        aria-label={t('carousel.next')}
                        size="small"
                        onClick={go(1)}
                        sx={carouselArrowSx('right', { alwaysVisible: true })}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 0,
                            right: 0,
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        {media.map((item, i) => (
                            <Box
                                key={`${item.type}-${item.url}`}
                                data-testid="presentation-media-dot"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: (theme) =>
                                        i === active
                                            ? theme.palette.common.white
                                            : alpha(theme.palette.common.white, 0.6),
                                }}
                            />
                        ))}
                    </Stack>
                </>
            )}
        </Box>
    );
}

/**
 * `presentation-video` reste le testid du bloc vidéo (contrat déjà couvert par
 * les scénarios `dmc-catalog-coverage`) ; l'iframe porte son propre testid pour
 * pouvoir asserter l'URL embed.
 */
function VideoSlide({ url, title }: { url: string; title: string }) {
    return (
        <Box data-testid="presentation-video" sx={{ position: 'absolute', inset: 0 }}>
            <Box
                component="iframe"
                data-testid="presentation-video-iframe"
                src={url}
                title={title}
                sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </Box>
    );
}
